export type AutomationMode = 'MANUAL' | 'AUTOPILOT' | 'SELF_DRIVING';

export type AutopilotActionStatus =
	| 'DETECTED'
	| 'ANALYZING'
	| 'PENDING_APPROVAL'
	| 'APPROVED'
	| 'EXECUTING'
	| 'COMPLETED'
	| 'DISMISSED'
	| 'STALE'
	| 'FAILED'
	| 'SECURITY_BLOCKED'
	| 'RESOLVED_ELSEWHERE';

export type AutopilotActionRisk = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type AutopilotActionUrgency = 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';

export interface AutopilotCitation {
	id: string;
	label: string;
	sourceType?: string | null;
	url?: string | null;
	documentName?: string | null;
	location?: string | null;
	projectName?: string | null;
	version?: string | null;
}

export type BidderResponseSourceCoverageCategory =
	| 'SCOPE_CHECKLIST'
	| 'DRAWINGS'
	| 'SPECIFICATIONS'
	| 'SUPPLEMENTAL_BID_DOCUMENTS';

export type BidderResponseSourceCoverageStatus =
	| 'CHECKED_MATCH'
	| 'CHECKED_NO_MATCH'
	| 'NOT_PRESENT'
	| 'UNAVAILABLE';

export interface BidderResponseSourceCoverage {
	category: BidderResponseSourceCoverageCategory;
	status: BidderResponseSourceCoverageStatus;
	message?: string | null;
}

export interface AutopilotActionComment {
	id: number | string;
	authorName: string;
	body: string;
	createdAt: string;
}

export interface AutopilotPreviewItem {
	id?: number | string;
	title?: string;
	label?: string;
	text?: string;
	displayText?: string;
	requirementText?: string;
	approvedDescription?: string;
	description?: string;
	pricingStatus?: 'PRICED' | 'TBD' | string;
	sourcePriceSummary?: string;
	missingOrDifferingRequirementFound?: string;
	suggestedChecklistAddition?: string;
	before?: unknown;
	after?: unknown;
	selected?: boolean;
	[key: string]: unknown;
}

export type BidderActionItemMessageSourceMode = 'RESEND_LAST' | 'SEND_CURRENT';
export type BidderActionItemMessageReadiness = 'READY' | 'STALE' | 'NEEDS_ATTENTION';

export interface BidderActionItemMessageContact {
	id: number | string;
	name?: string;
	email?: string;
	selected?: boolean;
}

export interface BidderActionItemMessageItem {
	id: number | string;
	label?: string;
	question?: string;
	messageText?: string;
	text?: string;
	displayText?: string;
	requirementText?: string;
	selected?: boolean;
	sourceIds?: Array<number | string>;
}

export interface BidderActionItemMessageSourceOption {
	available?: boolean;
	readiness?: BidderActionItemMessageReadiness;
	reasonCodes?: string[];
	sourceModeReason?: string;
	subject?: string;
	body?: string;
	actionItems?: BidderActionItemMessageItem[];
}

export interface BidderActionItemMessageRow extends AutopilotPreviewItem {
	id: number | string;
	invitationId?: number | string;
	submissionId?: number | string;
	actionItemRunId?: number | string | null;
	bidderCompanyId?: number | string;
	bidderName?: string;
	bidderCompanyName?: string;
	selected?: boolean;
	expanded?: boolean;
	readiness?: BidderActionItemMessageReadiness;
	sourceMode?: BidderActionItemMessageSourceMode;
	sourceModeReason?: string;
	sourceOptions?: Partial<
		Record<BidderActionItemMessageSourceMode, BidderActionItemMessageSourceOption>
	>;
	authorizedContacts?: BidderActionItemMessageContact[];
	subject?: string;
	body?: string;
	bodyEdited?: boolean;
	actionItems?: BidderActionItemMessageItem[];
	assumptions?: string[];
	reasonCodes?: string[];
}

export interface BidderActionItemMessagePackageChoice {
	id?: number | string;
	bidPackageId?: number | string;
	name?: string;
	packageName?: string;
	label?: string;
	confidence?: number | string;
	matchingBidderCount?: number;
	readiness?: BidderActionItemMessageReadiness;
	reason?: string;
	reasonCodes?: string[];
	selected?: boolean;
	variantFingerprint?: string;
}

export interface BidderActionItemMessagePackageVariant {
	bidPackageId: number | string;
	packageName?: string;
	matchingBidderCount?: number;
	readiness?: BidderActionItemMessageReadiness;
	reasonCodes?: string[];
	rows: BidderActionItemMessageRow[];
	variantFingerprint?: string;
}

export interface BidderActionItemMessageBatchPreview extends AutopilotStructuredPreview {
	kind: 'BIDDER_ACTION_ITEM_MESSAGE_BATCH';
	projectId?: number | string;
	projectName?: string;
	bidPackageId?: number | string;
	packageName?: string;
	packageChoices?: BidderActionItemMessagePackageChoice[];
	packageVariants?: BidderActionItemMessagePackageVariant[];
	readiness?: BidderActionItemMessageReadiness;
	reasonCodes?: string[];
	assumptionSummary?: string;
	assumptions?: string[];
	matchingBidderCount?: number;
	rows: BidderActionItemMessageRow[];
}

export interface AutopilotStructuredPreview {
	kind?: string;
	title?: string;
	summary?: string;
	text?: string;
	message?: string;
	before?: unknown;
	after?: unknown;
	options?: {
		acceptSuggestedTrades?: boolean;
		createBidLevelingChecklists?: boolean;
		proceedWithBidListAgent?: boolean;
		[key: string]: unknown;
	};
	items?: AutopilotPreviewItem[];
	rows?: AutopilotPreviewItem[];
	sourceCoverage?: BidderResponseSourceCoverage[] | { drawings?: string; specifications?: string };
	recipientSummary?: {
		excluded?: Array<{
			companyName?: string;
			contactName?: string | null;
			email?: string | null;
			invitationId?: number | string;
			reason?: string | null;
			vendorContactId?: number | string | null;
		}>;
		label?: string;
		recipients?: Array<{
			companyName?: string;
			contactName?: string | null;
			email?: string | null;
			invitationId?: number | string;
			selected?: boolean;
			status?: string | null;
			vendorContactId?: number | string | null;
		}>;
	};
	[key: string]: unknown;
}

export type AutopilotActionPreview = string | AutopilotStructuredPreview;

export interface AutopilotLinkedRun {
	id?: number | string | null;
	type?: string | null;
	status?: string | null;
	stage?: string | null;
	message?: string | null;
}

export interface AutopilotAffectedResource {
	type: string;
	refreshHint?: string | null;
	id?: number | string | null;
	projectId?: number | null;
	bidPackageId?: number | null;
}

export interface AutopilotAction {
	id: number | string;
	version: number;
	type: string;
	status: AutopilotActionStatus;
	title: string;
	question?: string | null;
	summary?: string | null;
	reason?: string | null;
	impact?: string | null;
	preview?: AutopilotActionPreview | null;
	projectId?: number | null;
	projectName?: string | null;
	parentActionId?: number | null;
	step?: number | null;
	stepLabel?: string | null;
	bidPackageId?: number | null;
	bidPackageName?: string | null;
	bidderName?: string | null;
	canonicalKey?: string | null;
	triggerLabel?: string | null;
	triggerType?: string | null;
	urgency?: AutopilotActionUrgency | null;
	createdAt: string;
	updatedAt?: string | null;
	presentedAt?: string | null;
	seenAt?: string | null;
	deadlineAt?: string | null;
	confidence?: number | null;
	risk?: AutopilotActionRisk | null;
	responseRequired?: boolean;
	retryAvailable?: boolean;
	acknowledgementAvailable?: boolean;
	undoAvailable?: boolean;
	errorMessage?: string | null;
	errorCode?: string | null;
	blockedReason?: string | null;
	citations?: AutopilotCitation[];
	comments?: AutopilotActionComment[];
	modeSnapshot?: AutomationMode | null;
	navigation?: {
		projectId?: number | null;
		bidPackageId?: number | null;
		actionItemRunId?: number | null;
		section?: string | null;
	} | null;
	decision?: string | null;
	decisionReason?: string | null;
	decidedAt?: string | null;
	decidedByName?: string | null;
	description?: string | null;
	executionResult?: unknown;
	executionStage?: string | null;
	executionMessage?: string | null;
	linkedRuns?: AutopilotLinkedRun[];
	affectedResources?: Array<AutopilotAffectedResource | string>;
	resolvedByName?: string | null;
}

export type AutopilotMessageRole = 'ASSISTANT' | 'USER' | 'SYSTEM';
export type AutopilotMessageStatus = 'SENDING' | 'SENT' | 'FAILED' | 'STREAMING';

export type AutopilotArtifactStatus = 'PENDING' | 'GENERATING' | 'READY' | 'FAILED' | 'DELETED';

export interface AutopilotArtifact {
	id: number | string;
	fileName: string;
	format: 'DOCX' | 'PDF' | 'PPTX' | 'XLSX';
	version: number;
	status: AutopilotArtifactStatus;
	previewStatus: AutopilotArtifactStatus;
	createdAt: string;
	errorMessage?: string | null;
}

export interface AutopilotMessage {
	id: number | string;
	conversationId: number | string;
	role: AutopilotMessageRole;
	content: string;
	createdAt: string;
	status?: AutopilotMessageStatus;
	projectId?: number | null;
	citations?: AutopilotCitation[];
	errorMessage?: string | null;
	retryContent?: string | null;
	artifacts?: AutopilotArtifact[];
}

export interface ProjectAutomationSettings {
	projectId: number;
	organizationId?: number | null;
	mode: AutomationMode;
	version: number;
	updatedAt?: string | null;
	updatedByName?: string | null;
	selfDrivingAcknowledgedAt?: string | null;
}

export interface OrganizationAutomationSettings {
	organizationId: number;
	selfDrivingAllowed: boolean;
	version: number;
}

export interface AutopilotAuthorizedOrganization {
	organizationId: number;
	organizationName: string;
}

export interface AutopilotBootstrapPage {
	actions: AutopilotAction[];
	messages: AutopilotMessage[];
	projectSettings: ProjectAutomationSettings[];
	organizationSettings?: OrganizationAutomationSettings | OrganizationAutomationSettings[] | null;
	authorizedOrganizations?: AutopilotAuthorizedOrganization[];
	conversationId?: number | string | null;
	nextCursor?: string | number | null;
	serverTime?: string | null;
	updatesCursor?: string | null;
	selfDrivingReleaseEnabled?: boolean;
}

export interface AutopilotUpdatePage {
	actions?: AutopilotAction[];
	messages?: AutopilotMessage[];
	projectSettings?: ProjectAutomationSettings[];
	organizationSettings?: OrganizationAutomationSettings | OrganizationAutomationSettings[] | null;
	authorizedOrganizations?: AutopilotAuthorizedOrganization[];
	conversationId?: number | string | null;
	serverTime?: string | null;
	updatesCursor?: string | null;
	selfDrivingReleaseEnabled?: boolean;
}

export interface AutopilotRecentActionsPage {
	actions: AutopilotAction[];
	nextCursor?: string | null;
	serverTime?: string | null;
}

export interface SelfDrivingAcknowledgement {
	acknowledged: true;
	policyVersion: string;
	warningAcceptedAt: string;
}

export interface AutopilotActionCommand {
	version: number;
	editedPreview?: AutopilotActionPreview;
	comment?: string;
}

export interface AutopilotConversationResponse {
	conversationId: number | string;
	messages: AutopilotMessage[];
	action?: AutopilotAction | null;
}

export const ACTIONABLE_AUTOPILOT_STATUSES: AutopilotActionStatus[] = [
	'PENDING_APPROVAL',
	'STALE',
	'FAILED',
	'SECURITY_BLOCKED',
];

export const isPendingApproval = (action: AutopilotAction): boolean =>
	action.status === 'PENDING_APPROVAL';

export const requiresAutopilotAttention = (action: AutopilotAction): boolean =>
	ACTIONABLE_AUTOPILOT_STATUSES.includes(action.status);
